// O limite do Grátis visto pelo jogo (item 59): o /api/eu ao abrir, ao voltar para a aba (o pagamento pode ter
// confirmado em outra aba) e ao sair de uma partida, e a mensagem limite que a mesa manda quando a partida começa.
// O selo, o menu, a aba Jogar e a mesa leem daqui, sem recarregar a página.
import { useSyncExternalStore } from 'react';
import type { OndeDoAvisoDoLimite, OrigemDoToque, Plano } from '@truco6/planos';
import { api, lerEu, type SituacaoLimite } from './api.ts';
import type { Sessao } from './conta.ts';
import { medir } from './medicao.ts';
import { ir, type Rota } from './rotas.ts';

let atual: SituacaoLimite | null = null;
let codigoDeIndicacao: string | null = null; // o código de indicação da conta, para o link de convite da sala
let sessaoAtual: Sessao | null = null;
let lendo: Promise<void> | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => { for (const f of ouvintes) f(); };

function definir(s: SituacaoLimite | null): void {
  atual = s;
  avisar();
}

// Lê de novo o /api/eu (uma leitura por vez). Erro de rede mantém o que já se sabe.
export function atualizarLimite(): Promise<void> {
  const s = sessaoAtual;
  if (!s) return Promise.resolve();
  lendo ??= lerEu(s).then((eu) => {
    if (sessaoAtual !== s) return;
    codigoDeIndicacao = eu.indicacao?.codigo ?? null;
    definir(eu.limite);
  }, () => {}).finally(() => { lendo = null; });
  return lendo;
}

// Liga o limite para a conta da aba; devolve a função que desliga.
export function iniciarLimite(s: Sessao): () => void {
  if (sessaoAtual?.id !== s.id) atual = null;
  sessaoAtual = s;
  void atualizarLimite();
  const voltou = () => { if (document.visibilityState === 'visible') void atualizarLimite(); };
  document.addEventListener('visibilitychange', voltou);
  return () => {
    document.removeEventListener('visibilitychange', voltou);
    if (sessaoAtual === s) { sessaoAtual = null; atual = null; }
  };
}

// A mesa contou a partida que começou (ou a vaga em que a pessoa sentou): quantas sobram hoje e de onde saiu.
export function limiteDaMesa(restantes: number, origem: 'diaria' | 'bonus'): void {
  const a = atual;
  if (!a || a.limite === null) { void atualizarLimite(); return; }
  const bonus = origem === 'bonus' ? restantes : Math.min(a.bonus, restantes);
  definir({ ...a, restantes, bonus, usadas: Math.min(a.limite, a.limite - (restantes - bonus)) });
}

export const meuCodigoDeIndicacao = () => codigoDeIndicacao;

// Saída da mesa (item 69): quem está no Grátis sem nenhuma partida hoje (nem bônus) vai para a página do limite; com 1
// sobrando, volta à aba Jogar e o selo do topo pisca uma vez (item 68). Os outros, à aba Jogar.
let piscarSelo = false;
export function destinoAoSairDaMesa(): Rota {
  const a = atual;
  if (a && a.limite !== null && a.restantes !== null) {
    if (a.restantes <= 0) return { tela: 'limite' };
    if (a.restantes === 1) piscarSelo = true;
  }
  return { tela: 'inicio' };
}
// Tentar jogar sem partidas (item 69): toda entrada numa mesa (sala da lista, código, chamado de amigo, mesa de
// comunidade e sala do campeonato) leva à página do limite. Assistir segue liberado.
export const semPartidasAgora = () => !!atual && atual.limite !== null && atual.restantes !== null && atual.restantes <= 0;
export function irParaMesa(r: Rota): void { ir(semPartidasAgora() ? { tela: 'limite' } : r); }
export function tirarPiscarSelo(): boolean {
  const p = piscarSelo;
  piscarSelo = false;
  return p;
}

// Para os testes de unidade e a vitrine.
export function limiteFixo(s: SituacaoLimite | null): void { definir(s); }

export function useLimite(): SituacaoLimite | null {
  return useSyncExternalStore((f) => { ouvintes.add(f); return () => ouvintes.delete(f); }, () => atual, () => atual);
}

// Quem está no Grátis, pelo plano do perfil e pelo que o /api/eu disse por último.
export const noGratis = (plano: Plano, l: SituacaoLimite | null) => plano === 'gratis' && (l === null || l.limite !== null);
export const semPartidas = (l: SituacaoLimite | null) => l !== null && l.restantes !== null && l.restantes <= 0;
// As partidas do dia que ainda sobram (sem o bônus).
export const diariasRestantes = (l: SituacaoLimite) => (l.limite === null ? 0 : Math.max(0, l.limite - l.usadas));

// Medidas: o GA4 recebe o evento (sem dados pessoais) e o painel recebe o lugar, pela API.
function medirNaApi(corpo: Record<string, string>): void {
  const s = sessaoAtual;
  if (!s || s.anonimo) return;
  void api(s, 'POST', '/medida', corpo).catch(() => {});
}
export function avisoDoLimiteVisto(onde: OndeDoAvisoDoLimite): void {
  medir('limite_aviso_visto', { onde });
  medirNaApi({ tipo: 'limite_aviso_visto', onde });
}
export function toqueEmAssinar(origem: OrigemDoToque): void {
  medir('assinar_toque', { origem });
  medirNaApi({ tipo: 'assinar_toque', origem });
}
export function toqueEmIndique(origem: OrigemDoToque): void {
  medir('indique_toque', { origem });
  medirNaApi({ tipo: 'indique_toque', origem });
}
